import React, { useEffect, useState } from 'react';
import {
  Zap,
  ShieldCheck,
  WifiOff,
  Share2,
  Shuffle,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { getScheduleCount } from '../firebase';
import StoreBadge from '../components/StoreBadge';

function FeatureCard({ icon: Icon, title, body, tint }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-card-hover dark:border-gray-800 dark:bg-gray-900/60">
      <div
        className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${tint}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-display text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-50">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
        {body}
      </p>
    </div>
  );
}

export default function Marketing() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    try {
      getScheduleCount((c) => setCount(c));
    } catch (err) {
      setCount(null);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-surface-950 dark:text-gray-100">
      {/* Nav */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <a href="/" className="flex items-center gap-2">
          <img
            src="/logo192.png"
            alt=""
            className="h-8 w-8 rounded-lg shadow-card"
          />
          <span className="font-display text-lg font-semibold tracking-tight">
            SMPW Scheduler
          </span>
        </a>
        <nav className="hidden items-center gap-6 text-sm text-gray-600 dark:text-gray-400 sm:flex">
          <a href="#features" className="hover:text-brand-600 dark:hover:text-brand-400">
            Features
          </a>
          <a href="#how" className="hover:text-brand-600 dark:hover:text-brand-400">
            How it works
          </a>
          <a href="/support" className="hover:text-brand-600 dark:hover:text-brand-400">
            Support
          </a>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-brand-50/70 via-white to-white dark:from-brand-900/20 dark:via-surface-950 dark:to-surface-950"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 -z-10 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-brand-200/40 blur-3xl dark:bg-brand-500/10"
        />

        <div className="mx-auto grid max-w-6xl gap-10 px-6 pt-10 pb-16 md:grid-cols-2 md:items-center md:pt-16 md:pb-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:border-brand-800/50 dark:bg-brand-900/30 dark:text-brand-300">
              <Sparkles className="h-3.5 w-3.5" />
              Free. No account needed.
            </span>
            <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl md:text-6xl dark:text-gray-50">
              A simple{' '}
              <span className="bg-gradient-to-r from-brand-500 to-accent-400 bg-clip-text text-transparent">
                schedule maker
              </span>{' '}
              for your group.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-gray-600 dark:text-gray-400">
              Add your volunteers and generate a schedule. Shuffle or edit
              until it looks right, then send it to the group as a clean
              image.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <StoreBadge platform="ios" />
              <StoreBadge platform="android" />
            </div>
            <div className="mt-4">
              <a
                href="/"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 dark:text-brand-300 dark:hover:text-brand-200"
              >
                Or open it in your browser
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>

            {count !== null && count > 0 && (
              <div className="relative mt-8 inline-flex items-center gap-4 overflow-hidden rounded-2xl border border-brand-200/70 bg-gradient-to-br from-brand-50 via-white to-accent-50 px-5 py-4 shadow-card backdrop-blur dark:border-brand-800/40 dark:from-brand-900/40 dark:via-surface-900/60 dark:to-accent-500/10">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-accent-300/30 blur-3xl dark:bg-accent-400/10"
                />
                <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                </span>
                <div className="relative flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">
                    Live
                  </span>
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-display text-3xl font-semibold tracking-tight tabular-nums sm:text-4xl bg-gradient-to-r from-brand-600 to-accent-500 bg-clip-text text-transparent dark:from-brand-300 dark:to-accent-300">
                      {count.toLocaleString()}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      schedules built and counting
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-br from-brand-400/20 to-accent-300/20 blur-2xl" />
            <img
              src="/SMPW Hero.png"
              alt="SMPW Scheduler — a simple schedule maker for your group"
              className="w-full rounded-2xl border border-gray-200/70 shadow-elevated dark:border-gray-800"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            A few small things it does well.
          </h2>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Open it up and start adding names.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <FeatureCard
            icon={Zap}
            title="Quick to adjust"
            body="Enter names once, then shuffle and fine-tune until the assignments look right."
            tint="bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
          />
          <FeatureCard
            icon={ShieldCheck}
            title="Private by default"
            body="No accounts. No analytics. No ads. Names never leave your device."
            tint="bg-accent-100 text-accent-500 dark:bg-accent-500/20 dark:text-accent-300"
          />
          <FeatureCard
            icon={WifiOff}
            title="Works offline"
            body="Build schedules on the train, in the backyard, anywhere. No signal required."
            tint="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
          />
          <FeatureCard
            icon={Share2}
            title="Share as an image"
            body="Export a crisp PNG and send it by message, email, or your group chat — no app needed to view."
            tint="bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
          />
        </div>
      </section>

      {/* How it works */}
      <section
        id="how"
        className="border-y border-gray-200/70 bg-gray-50/60 py-16 dark:border-gray-800 dark:bg-surface-900/40 md:py-24"
      >
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Three steps.
            </h2>
          </div>

          <ol className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                n: '01',
                title: 'Add volunteers',
                body:
                  'Type names, or tap the presets. Mix and match any combination you need.',
              },
              {
                n: '02',
                title: 'Shuffle',
                body:
                  'Smart assignment spreads people evenly across slots. Tap Shuffle again if you want a different mix.',
              },
              {
                n: '03',
                title: 'Save or share',
                body:
                  'Generate a clean image. Save to Photos, AirDrop, text, or email.',
              },
            ].map((step) => (
              <li
                key={step.n}
                className="relative rounded-2xl bg-white p-6 shadow-card dark:bg-gray-900/60"
              >
                <span className="font-display text-sm font-semibold tracking-widest text-brand-600 dark:text-brand-400">
                  {step.n}
                </span>
                <h3 className="mt-2 font-display text-xl font-semibold tracking-tight">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 p-10 text-white shadow-elevated md:p-14">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-accent-400/20 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -left-24 -bottom-24 h-80 w-80 rounded-full bg-brand-300/20 blur-3xl"
          />
          <div className="relative max-w-2xl">
            <Shuffle className="h-8 w-8 text-brand-200" />
            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Give it a try.
            </h2>
            <p className="mt-4 text-brand-50/90">
              Free on the App Store and Google Play. No account, nothing to
              set up — just open it and start adding names.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <StoreBadge platform="ios" />
              <StoreBadge platform="android" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200/70 py-10 dark:border-gray-800">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 text-sm text-gray-500 dark:text-gray-400 sm:flex-row">
          <p> {new Date().getFullYear()} SMPW Scheduler</p>
          <div className="flex items-center gap-5">
            <a href="/" className="hover:text-brand-600 dark:hover:text-brand-400">
              App
            </a>
            <a
              href="/privacy"
              className="hover:text-brand-600 dark:hover:text-brand-400"
            >
              Privacy
            </a>
            <a
              href="/support"
              className="hover:text-brand-600 dark:hover:text-brand-400"
            >
              Support
            </a>
            <a
              href="https://donate.stripe.com/aFaeVc40j8K6doq7ge0kE00"
              target="_blank"
              rel="noreferrer"
              className="hover:text-brand-600 dark:hover:text-brand-400"
            >
              Donate
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
